export interface SizeRequest {
  userCount: number;
  width: number;
  height: number;
}

export const ARROW_WIDTH = 44;
const ARROW_GAP = 12;
const ARROW_GAP_MIN = 8;

const TILE_PADDING_TOP = 20;
const TILE_PADDING_BOTTOM = 20;
const TILE_PADDING_LEFT = 20;
const TILE_PADDING_RIGHT = 20;

const TILE_PADDING_TOP_MIN = 12;
const TILE_PADDING_BOTTOM_MIN = 12;
const TILE_PADDING_LEFT_MIN = 12;
const TILE_PADDING_RIGHT_MIN = 12;

const TILE_GAP = 24;
const TILE_GAP_MIN = 12;

const MAX_PER_PAGE = 9;

export interface SizeResponse {
  padding: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  gap: number;
  arrowGap: number;
  width: number;
  height: number;
  userPerPage: number;
  pages: number;
}

export function maxWH(availableW: number, availableH: number) {
  if (availableH * (16 / 9) > availableW) {
    return {
      width: availableW,
      height: availableW * (9 / 16),
    };
  } else {
    return {
      width: availableH * (16 / 9),
      height: availableH,
    };
  }
}

export function getSizes(req: SizeRequest): SizeResponse {
  const settings = {
    padding: {
      top: TILE_PADDING_TOP,
      bottom: TILE_PADDING_BOTTOM,
      left: TILE_PADDING_LEFT,
      right: TILE_PADDING_RIGHT,
    },
    gap: TILE_GAP,
    arrowGap: ARROW_GAP,
    maxRowLength: 1,
    maxColLength: 1,
  };

  if (req.width <= 960 || req.height <= 540) {
    settings.padding.top = TILE_PADDING_TOP_MIN;
    settings.padding.bottom = TILE_PADDING_BOTTOM_MIN;
    settings.padding.left = TILE_PADDING_LEFT_MIN;
    settings.padding.right = TILE_PADDING_RIGHT_MIN;
    settings.gap = TILE_GAP_MIN;
    settings.arrowGap = ARROW_GAP_MIN;
  }

  // считаем что мин ширина плитки = 300 (это условно, начиная от 2 в ряд, при одной в ряд, просто по ширине)
  // доступная ширина от 0 до 740 - одна плитка шириной от 0 до 612
  // доступная ширина от 740 до 1100 - две плитки шириной от 300 до 462
  // доступная ширина от 1100 до 1424 - три плитки шириной от 300 до 408 (бесконечно если не выбран лаяут 4x2)
  // доступная ширина больше 1424 - три или четыре плитки шириной от 300 до бесконечно
  if (req.width < 740) {
    settings.maxRowLength = 1;
  } else if (req.width < 1100) {
    settings.maxRowLength = 2;
  } else if (req.width < 1424) {
    settings.maxRowLength = 3;
  } else {
    settings.maxRowLength = 4;
  }

  // считаем что мин высота плитки = 168.75 (при ширине 300px)
  // доступная высота от 0 до 364 - одна плитка высотой от 0 до 339
  // доступная высота от 364 до 595 - две плитки высотой от 168.75 до 265
  // доступная высота от 595 до 787 - три плитки высотой от 168.75 до 233 (либо бесконечно если 3x3)
  // доступная высота больше 787 - три или четыре плитки высотой от 168.75 до бесконечно
  if (req.height < 364) {
    settings.maxColLength = 1;
  } else if (req.height < 595) {
    settings.maxColLength = 2;
  } else if (req.height < 787) {
    settings.maxColLength = 3;
  } else {
    settings.maxColLength = 4;
  }
  const availableLayouts = [] as {
    rowLength: number;
    colLength: number;
    area: number;
    pages: number;
    capacity: number;
    realUsersInPage: number;
    size: {
      width: number;
      height: number;
    };
    score: number;
  }[];
  for (let i = 1; i <= settings.maxRowLength; i++) {
    for (let j = 1; j <= settings.maxColLength; j++) {
      const capacity = i * j;
      // Скипаем лаяуты с большим количеством пользователей (4x4 или 4x3)
      if (capacity > MAX_PER_PAGE) continue;
      if (i === 1 && j === 1) continue;
      const pages = Math.ceil(req.userCount / capacity);
      const realUsersInPage = Math.min(req.userCount, capacity);
      const arrowsWidth =
        pages > 1 ? 2 * ARROW_WIDTH + 2 * settings.arrowGap : 0;
      const maxW =
        (req.width -
          settings.padding.left -
          settings.padding.right -
          arrowsWidth -
          (i - 1) * settings.gap) /
        i;
      const maxH =
        (req.height -
          settings.padding.top -
          settings.padding.bottom -
          (j - 1) * settings.gap) /
        j;
      const realSize = maxWH(maxW, maxH);
      const area = realSize.width * realSize.height * realUsersInPage;
      const score = area / pages;
      availableLayouts.push({
        rowLength: i,
        colLength: j,
        area,
        pages,
        capacity,
        realUsersInPage,
        score,
        size: realSize,
      });
    }
  }
  availableLayouts.sort((a, b) => b.score - a.score);
  const best = availableLayouts[0];
  if (best) {
    return {
      gap: settings.gap,
      arrowGap: settings.arrowGap,
      padding: settings.padding,
      width: best.size.width * 0.99,
      height: best.size.height * 0.99,
      userPerPage: best.realUsersInPage,
      pages: best.pages,
    };
  } else {
    return {
      gap: settings.gap,
      arrowGap: settings.arrowGap,
      padding: settings.padding,
      width:
        req.width -
        settings.padding.left -
        settings.padding.right -
        2 * ARROW_WIDTH -
        2 * settings.arrowGap,
      height: req.height - settings.padding.top - settings.padding.bottom,
      userPerPage: 1,
      pages: req.userCount,
    };
  }
}
